
import { useCallback } from 'react';
import { BookingDetails } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { useBooking } from '@/contexts/BookingContext';
import { useAuth } from '@/contexts/AuthContext';

export function useBookingService() {
  const { toast } = useToast();
  const { clearBooking } = useBooking();
  const { user } = useAuth();

  const createBooking = useCallback(async (bookingDetails: BookingDetails) => {
    try {
      console.log('Creating booking with details:', bookingDetails);
      
      // Determine user context - authenticated or guest
      const isAuthenticated = !!user;
      const userId = user?.id || crypto.randomUUID(); // Generate temporary ID for guests
      const userEmail = user?.email || bookingDetails.customerEmail;
      
      // Prepare booking data for database
      const bookingData = {
        user_id: userId,
        plant_count: bookingDetails.plantCount,
        service_type: bookingDetails.serviceType,
        frequency: bookingDetails.frequency,
        price_per_visit: bookingDetails.pricePerVisit,
        monthly_price: bookingDetails.price,
        address: bookingDetails.address,
        zip_code: bookingDetails.zipCode,
        zone: bookingDetails.zone,
        access_info: bookingDetails.accessInfo || '',
        special_instructions: bookingDetails.specialInstructions || '',
        customer_name: bookingDetails.customerName,
        customer_email: userEmail,
        customer_phone: bookingDetails.customerPhone,
        status: isAuthenticated ? 'confirmed' : 'pending_auth',
        created_at: new Date().toISOString()
      };
      
      console.log('Inserting booking data:', { 
        ...bookingData, 
        isAuthenticated,
        userType: isAuthenticated ? 'authenticated' : 'guest'
      });
      
      // Insert booking record
      const { data, error } = await supabase
        .from('bookings')
        .insert(bookingData)
        .select()
        .maybeSingle();
        
      if (error) {
        console.error('Error creating booking:', error);
        
        // Handle specific RLS errors for guest users
        if (error.message.includes('row-level security') && !isAuthenticated) {
          toast({
            title: "Authentication Required",
            description: "Please sign up or sign in to complete your booking. Your booking details will be saved.",
            variant: "destructive"
          });
          return { success: false, error, requiresAuth: true };
        }
        
        toast({
          title: "Booking Failed",
          description: `Database error: ${error.message}. Please try again or contact support.`,
          variant: "destructive"
        });
        return { success: false, error };
      }
      
      console.log('Booking created successfully:', data);
      
      // If booking has a scheduled date and time, create a service visit
      if (bookingDetails.date && bookingDetails.timeSlot && isAuthenticated) {
        const visitData = {
          userid: userId,
          plantcount: bookingDetails.plantCount,
          servicetype: bookingDetails.serviceType,
          date: bookingDetails.date.toISOString(),
          status: 'scheduled',
          address: bookingDetails.address,
          zipcode: bookingDetails.zipCode,
          zone: bookingDetails.zone,
          timeslot: bookingDetails.timeSlot,
        };
        
        console.log('Creating service visit:', visitData);
        
        const { error: visitError } = await supabase
          .from('service_visits')
          .insert(visitData);
          
        if (visitError) {
          console.error('Error creating service visit:', visitError);
          toast({
            title: "Visit Scheduling Failed",
            description: "Your booking was created but we couldn't schedule your visit. Our team will contact you.",
            variant: "destructive"
          });
        } else {
          console.log('Service visit created successfully');
        }
      }
      
      // Create/update user profile for authenticated users
      if (isAuthenticated && user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
              id: user.id,
              email: user.email || bookingDetails.customerEmail,
              name: bookingDetails.customerName,
              phone: bookingDetails.customerPhone,
              address: bookingDetails.address,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'id'
            });
            
          if (profileError) {
            console.error('Error updating user profile:', profileError);
            // Don't fail the booking for profile errors
          } else {
            console.log('User profile updated successfully');
          }
        } catch (profileError) {
          console.error('Profile update failed:', profileError);
          // Continue with booking success even if profile update fails
        }
      }
      
      toast({
        title: "Booking Successful!",
        description: isAuthenticated 
          ? "Your plant care service has been booked. You'll receive a confirmation email shortly."
          : "Your booking request has been submitted. Please sign up to schedule your service and receive confirmation.",
      });
      
      clearBooking();
      return { 
        success: true, 
        data, 
        isAuthenticated,
        userId: user?.id,
        bookingId: data.id
      };
      
    } catch (error) {
      console.error('Unexpected error during booking:', error);
      toast({
        title: "Booking Failed",
        description: "An unexpected error occurred. Please try again or contact support at help@plantthis.net.",
        variant: "destructive"
      });
      return { success: false, error };
    }
  }, [toast, clearBooking, user]);

  return { createBooking };
}
